import { QueryKey, useInfiniteQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { useCallback, useMemo } from "react";
import { ApiResponse } from "src/api/public-types";

/* page token 방식(요청 pageToken, 응답 nextPageToken) 목록 API 한 페이지 정규화 */
export type TokenPage<TItem> = {
  items: TItem[];
  nextPageToken?: string;
  totalItemCount?: number;
};

/*
 * 토큰이 무효해졌다고 보는 응답 코드
 * eET0516 은 BFF 공통 "Invalid Data", 나머지는 RAD54 명세
 */
const DEAD_TOKEN_ERROR_CODES = new Set(["eET0516", "page_token_not_found", "page_token_query_mismatch", "page_token_expired"]);

const getErrorCode = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) return undefined;
  const { response } = error as AxiosError<ApiResponse<unknown>>;
  return response?.data?.meta?.errorCode;
};

/* 마지막 페이지까지 이어받기. 0건 페이지가 와도 토큰이 있으면 계속 받음 */
export const fetchAllFromPageToken = async <TItem>(
  fetchPage: (pageToken?: string) => Promise<TokenPage<TItem>>,
  startPageToken: string,
): Promise<TItem[]> => {
  const items: TItem[] = [];
  let pageToken: string | undefined = startPageToken;
  while (pageToken) {
    const page: TokenPage<TItem> = await fetchPage(pageToken);
    items.push(...page.items);
    pageToken = page.nextPageToken;
  }
  return items;
};

type Params<TItem> = {
  queryKey: QueryKey;
  fetchPage: (pageToken?: string) => Promise<TokenPage<TItem>>;
  enabled?: boolean;
};

export default function usePageTokenInfiniteQuery<TItem>({ queryKey, fetchPage, enabled = true }: Params<TItem>) {
  const query = useInfiniteQuery({
    queryKey,
    enabled,
    /* 재조회는 누적 페이지 수만큼 순차 요청이 나가므로 끔 */
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    /* 실패는 목록 안에서 보여주므로 전역 에러 모달을 띄우지 않음 */
    meta: { noErrorModal: true },
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    getNextPageParam: (lastPage: TokenPage<TItem>) => lastPage.nextPageToken ?? undefined,
  });

  /*
   * 첫 페이지 실패는 이어받을 토큰이 없으므로 처음부터 받음
   * 무효한 토큰은 재시도해도 계속 실패하므로 처음부터 받음
   */
  const { data, error, fetchNextPage, refetch } = query;
  const retryLoadMore = useCallback(() => {
    const errorCode = getErrorCode(error);
    const isDeadToken = errorCode !== undefined && DEAD_TOKEN_ERROR_CODES.has(errorCode);
    if (!data || isDeadToken) {
      refetch();
    } else {
      fetchNextPage();
    }
  }, [data, error, fetchNextPage, refetch]);

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const totalItemCount = data?.pages[0]?.totalItemCount;
  const nextPageToken = data?.pages[data.pages.length - 1]?.nextPageToken;

  /* 쓰는 속성만 꺼내야 react-query 의 변경 추적 최적화가 유지됨 */
  return {
    items,
    totalItemCount,
    nextPageToken,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage,
    retryLoadMore,
  };
}
